// BOXEB2B — Blog articles (French, SEO-optimized, ~600-900 words each).
// Content is honest, useful, and reflects the product positioning.

export const ARTICLES = [
    {
        slug: "prospecter-entreprises-guyane-2026",
        title: "Comment prospecter des entreprises en Guyane en 2026 : le guide complet",
        description:
            "Guide pratique pour trouver, contacter et convertir des entreprises B2B en Guyane (973) : sources légales, sectors clés, stratégie outreach et pièges à éviter.",
        keywords: "prospection Guyane, 973, B2B, entreprises Guyane, CCIG, data.gouv, SIREN, BTP Cayenne, Kourou",
        published_at: "2026-07-10",
        author: "Équipe BOXEB2B",
        cover_gradient: "from-emerald-500 to-brand-cyan",
        emoji: "🇬🇫",
        read_min: 6,
        body: `
La Guyane représente **~10 000 entreprises actives** dont environ 3 000 PME structurées. Un marché intime, sous-servi par les grands outils de prospection (Apollo, Cognism, ZoomInfo). Voici comment y aller intelligemment.

## Pourquoi la Guyane est un terrain de chasse sous-exploité

- **Concurrence quasi-nulle** : les SDR européens ignorent le marché → tes emails ne se noient pas dans du bruit
- **Décideurs accessibles** : dans 80% des PME guyanaises, le gérant est le décideur. Pas de gatekeeper à trois niveaux
- **Sectors en croissance** : BTP (chantier spatial Kourou), tourisme durable, filière bois, agroalimentaire, aquaculture

## Les sources légales pour trouver les leads

Trois sources et pas plus. Tout le reste est du scraping juridiquement gris.

1. **[recherche-entreprises.api.gouv.fr](https://recherche-entreprises.api.gouv.fr)** — LA base officielle INSEE. Gratuite, à jour, RGPD-clean. Contient SIREN, adresse, secteur NAF, dirigeants nommés, chiffre d'affaires public.
2. **Annuaire de la CCIG** — utile en consultation, mais pas d'export automatique. À utiliser pour compléter des fiches sur les gros comptes.
3. **LinkedIn Sales Navigator** — filtre par "Location: French Guiana" + secteur. Coût : ~80€/mois, mais couvre les décideurs qui ne sont pas dirigeants (directeurs commerciaux, RH, IT).

## Les 5 secteurs guyanais à cibler en priorité

| Secteur | Nb entreprises (siège 973) | Ticket moyen SaaS |
|---|---|---|
| BTP / Construction | ~900 | 200-800€/mois |
| Commerce & retail | ~1 200 | 100-500€/mois |
| Transport & logistique | ~350 | 300-1500€/mois |
| Restauration & hôtellerie | ~600 | 100-400€/mois |
| Services aux entreprises | ~500 | 200-1000€/mois |

## La stratégie outreach qui marche localement

**Ne copie pas ta séquence Paris/Berlin.** Le ton, les références, les preuves sociales — tout doit être adapté :

- **Mentionne le contexte local** : "Vu votre chantier à Rémire-Montjoly…" > "Bonjour, j'ai une solution pour vous"
- **Adapte les fuseaux** : GMT-3, tes emails partent à 14h Paris pour un 9h local
- **Utilise des références locales** : parle en francs guyanais mentaux (le rapport prix/perception est différent), cite des clients guyanais réels si tu en as

## Le piège numéro 1 : les emails introuvables

Sur 200 PME guyanaises importées, tu trouveras **environ 40-60 emails vraiment vérifiés** via Hunter.io. Les 140+ restants n'ont pas de site web indexé. Solutions honnêtes :

1. **Combo IA pattern + Hunter verify** : générer \`prenom.nom@societe.fr\` via IA (1 crédit), vérifier via Hunter (0.5 crédit) → 60% de délivrabilité pour 1.5c au lieu de 3
2. **Téléphone via Pages Jaunes** : moins de bruit, plus de réponse. Le call froid marche mieux en Guyane qu'en métropole
3. **LinkedIn InMail** : ~10€/message mais taux de réponse 3x supérieur à l'email

## Le workflow BOXEB2B pour la Guyane

1. Va sur **Find > Import > Starter Guyane** → 1 clic pour importer 200-400 leads réels
2. Va sur **Enrich > IA pattern** → génère les emails devinés (1c/lead)
3. Va sur **Enrich > Hunter.io** → vérifie ceux qui sont trouvables (3c/lead)
4. Va sur **Engage > New campaign** → l'IA écrit ta séquence en 3 étapes
5. Envoie 20 emails/jour max par domaine (préserve ta délivrabilité)

## Ce qu'on ne te dira jamais ailleurs

Le vrai avantage compétitif de la Guyane, ce n'est pas les 10 000 boîtes. C'est **le bouche-à-oreille**. Ferme 3 clients à Cayenne → dans 6 mois, tu as 15 introductions warm. Focalise ton outreach sur les 20 top-scorés, pas sur du volume.
        `,
    },
    {
        slug: "alternatives-upscaleb2b-pay-as-you-go-saas",
        title: "Alternatives à UpscaleB2B en 2026 : pourquoi les PME choisissent le pay-as-you-go",
        description:
            "UpscaleB2B, Cognism, Apollo — combien ça coûte vraiment, pour quel résultat, et pourquoi les PME européennes se tournent vers des solutions self-serve pay-as-you-go.",
        keywords: "alternative UpscaleB2B, prix Cognism, prix Apollo, lead gen SaaS PME, prospection B2B pas cher",
        published_at: "2026-07-08",
        author: "Équipe BOXEB2B",
        cover_gradient: "from-brand-indigo to-brand-violet",
        emoji: "⚡",
        read_min: 7,
        body: `
Si tu lis cet article, tu as probablement reçu un devis à 5 chiffres d'UpscaleB2B (ou d'un concurrent équivalent). Voici la comparaison brute, sans langue de bois.

## Combien coûte vraiment UpscaleB2B ?

UpscaleB2B ne publie pas ses prix. Basé sur les retours utilisateurs et devis leakés sur Reddit/G2 :

- **Setup fee** : 15 000 – 25 000 $
- **Abonnement annuel** : 30 000 – 60 000 $
- **Engagement** : 12 mois minimum
- **TCO année 1** : ~50 000 – 85 000 $

Ce que tu obtiens : une équipe de SDR externalisés qui envoient tes campagnes email + fournissent des leads scoré. Résultat marketing typique : 40-80 meetings qualifiés/an.

**Coût par meeting : ~800-1500 $.** Rentable si tu vends de l'enterprise à >100k€ le contrat. Ruineux si tu vends du SaaS PME à 100€/mois.

## Le vrai marché des alternatives

| Outil | Modèle | Prix entrée | Idéal pour |
|---|---|---|---|
| UpscaleB2B | Agence + outil | 40k$+ | Enterprise US |
| Cognism | SaaS Enterprise | 15k$/an | Mid-market |
| Apollo.io | SaaS + freemium | 0-99$/mois | SMB tech US |
| Lusha | SaaS credits | 39$/mois | Tech recruiters |
| Kaspr | SaaS crédits | 45$/mois | France uniquement |
| **BOXEB2B** | Pay-as-you-go | 0€ (50 crédits) | PME EU-first |

## Pourquoi le pay-as-you-go change tout

Le vieux modèle SaaS lead gen : tu paies un abonnement de 500€/mois, tu utilises 10% des features, tu ne peux pas partir avant 12 mois. Comme un abonnement salle de sport que tu prends en janvier.

Le pay-as-you-go BOXEB2B :

- **50 crédits gratuits** à l'inscription — tu testes sans t'engager
- **1 crédit = 1 email révélé** (~0.05€ effectif au pack Growth)
- **Aucun engagement** : tu utilises quand tu prospects, tu paies rien quand tu ne prospects pas
- **IA incluse** : la génération d'emails et le scoring sont gratuits, tu ne paies que les révélations

## Le calcul concret pour une PME qui prospecte 100 leads/mois

- UpscaleB2B : 50 000 € / an = **4 167 €/mois** pour 100 leads/mois (fully-managed)
- Apollo.io Team : 199 $/mois pour 5 000 crédits (tu utilises 20%)
- **BOXEB2B pay-as-you-go** : 100 emails × 1c + 100 vérifications × 0.5c = 150 crédits = ~12 € **au tarif Growth pack**

Le gap est de **~350x**. Même si tu ajoutes ton propre temps SDR à 30€/h × 8h/semaine, tu es à ~1000€/mois total contre 4167€.

## Ce que le SaaS pay-as-you-go **ne remplacera jamais**

Sois honnête : UpscaleB2B est meilleur si :

- Tu vends du **contrat à 500k€+** où chaque meeting compte
- Tu n'as **aucune ressource interne** pour piloter la prospection
- Tu veux **une équipe humaine dédiée** qui itère sur les campagnes

Le pay-as-you-go est meilleur si :

- Tu es **founder / SDR seul** ou petite équipe
- Tu veux **piloter toi-même** avec un copilote IA
- Tu vends du **ticket 50-5000€/mois** où le coût d'acquisition doit rester sous 200€

## L'erreur à ne pas faire

Ne compare pas les prix sans compter ton temps. Un outil moins cher qui te fait perdre 10h/semaine coûte plus qu'un outil managé. La bonne question : "combien de temps humain je remplace ?"

BOXEB2B remplace 3-5h/semaine de recherche + rédaction d'emails. À 30€/h, ça vaut 400-600€/mois pour toi. Le pack Growth (149€/mois) est rentable dès la 2ème semaine.
        `,
    },
    {
        slug: "siren-insee-rgpd-prospection-b2b-france",
        title: "SIREN, INSEE, RGPD : comprendre les sources légales de prospection B2B en France",
        description:
            "RGPD, INSEE, SIREN, annuaire des entreprises — le guide juridique et technique pour prospecter en B2B en France sans risquer une amende CNIL.",
        keywords: "RGPD prospection B2B, SIREN, INSEE, CNIL, prospection légale France, scraping légal, data.gouv",
        published_at: "2026-07-05",
        author: "Équipe BOXEB2B",
        cover_gradient: "from-amber-500 to-rose-500",
        emoji: "⚖️",
        read_min: 5,
        body: `
"Le RGPD interdit la prospection B2B" — non, c'est faux. Voici ce qui est vraiment autorisé, et où sont les pièges.

## Le principe RGPD pour le B2B en 2 phrases

Contrairement au B2C où tu as besoin d'un **consentement explicite**, le B2B fonctionne au **legitimate interest** (article 6-1-f du RGPD) : tu peux contacter une personne dans son contexte professionnel si :

1. Le message concerne son activité professionnelle (pas sa vie perso)
2. Tu offres un opt-out clair et facile (lien "se désabonner" dans chaque mail)
3. Tu ne conserves pas les données au-delà du raisonnable (3 ans max en général)

**Traduction concrète** : envoyer un email à \`jean.dupont@societe.fr\` pour lui proposer un outil professionnel = LÉGAL. Envoyer à \`jean.dupont@gmail.com\` (email perso) = ILLÉGAL sans opt-in.

## Les sources 100% légales pour prospecter en France

### 1. INSEE / recherche-entreprises.api.gouv.fr
La base officielle française. Contient :

- SIREN / SIRET
- Raison sociale, adresse, secteur NAF
- Dirigeants nommés (personnes physiques exerçant un mandat social)
- Effectif, date de création, chiffre d'affaires (si publié)

Statut légal : **totalement public** (loi PACTE 2019). Tu peux l'exporter, l'enrichir, le stocker.

### 2. Annuaire-entreprises.data.gouv.fr
Interface publique du même dataset INSEE. Consultation gratuite, exports limités.

### 3. Bulletin Officiel des Annonces Civiles et Commerciales (BODACC)
Les mouvements légaux (créations, cessions, procédures). Public, filtrable, utile pour cibler les créateurs récents.

### 4. Registres commerciaux locaux (CCI, Greffes)
Consultables mais pas exportables en masse sans accord.

## Les sources GRISES à éviter

- **Scraping LinkedIn** : ToS violé, ban définitif, contentieux en cours
- **Scraping Pages Jaunes** : ToS violé, blocages IP
- **Achats de bases de données emails** : parfaites illégales si le vendeur n'a pas obtenu le consentement (99% des cas)
- **Guessing puis envoi massif** : légal pour l'envoi si professionnel, mais nuit à ta délivrabilité (bounces = domaine blacklisté)

## Les 5 règles pour rester clean CNIL

1. **Emails professionnels uniquement** — pas d'@gmail, @outlook, @yahoo etc.
2. **Un opt-out par email** — lien de désinscription qui marche vraiment
3. **Un opt-out permanent** — quand quelqu'un demande à sortir, tu supprimes définitivement (pas juste "un an sans contact")
4. **Registre des traitements** — obligatoire au-delà de 250 leads, format libre
5. **Pas de tracking pixel invisible sans mention** — si tu utilises open tracking (BOXEB2B le fait), mentionne-le dans ta politique de confidentialité

## Ce que BOXEB2B garantit

- **Sources 100% publiques** (INSEE, gouv.fr uniquement)
- **Opt-out automatique** dans chaque email envoyé via Engage
- **GDPR center** dans Settings : export complet + suppression irréversible en 1 clic
- **Aucun IP stocké** pour le tracking des visiteurs

## Le cas particulier des DOM-COM

Guyane, Martinique, Guadeloupe, Mayotte, Réunion — même règles RGPD que la métropole. Aucune spécificité juridique. Les données INSEE couvrent tous les départements.

## Le coût d'une amende CNIL

Pour info : Vectaury (2018) → 400 000 € pour non-respect du consentement. Google → 50 M€ (2019).
Pour une PME, les amendes sont proportionnées (~10-50k€ typique en 2024-25) mais **la publicité négative fait beaucoup plus mal que la sanction**. Une mention "condamné par la CNIL" sur Google = -30% de conversion pendant 2 ans.
        `,
    },
    {
        slug: "sequence-email-b2b-8-pourcent-reponse",
        title: "10 façons d'écrire une séquence email B2B qui obtient 8%+ de réponse",
        description:
            "La cold email n'est pas morte. Voici les 10 techniques concrètes qui font passer un taux de réponse de 1% à 8-12%, avec exemples réels.",
        keywords: "cold email B2B, taux de réponse, séquence outbound, prospection email, exemples cold email français",
        published_at: "2026-07-02",
        author: "Équipe BOXEB2B",
        cover_gradient: "from-brand-cyan to-brand-indigo",
        emoji: "✉️",
        read_min: 6,
        body: `
La moyenne cold email B2B en 2026 : **1.2% de réponse**. La très bonne moyenne : **8-12%**. Ce qui distingue les deux, ce ne sont ni les outils ni le budget, mais 10 techniques que voici.

## 1. L'objet fait 60% du travail

Un bon objet ressemble à un SMS d'un collègue :

- ❌ "Solution innovante pour votre entreprise"
- ✅ "Question rapide sur [projet spécifique]"
- ✅ "5 min ?"
- ✅ "Vu votre article sur [X], une pensée"

**Règle** : max 5 mots, minuscules, aucune promesse commerciale, curiosité provoquée.

## 2. Personnalisation ≠ prénom en variable

Écrire "Bonjour Jean" au lieu de "Bonjour" ne change rien. La vraie personnalisation :

- Mentionne un **événement récent** (annonce, embauche, article)
- Mentionne une **contrainte spécifique** de leur métier
- Cite un **concurrent** qu'ils regardent

**Test facile** : si l'email peut être envoyé à 100 personnes différentes en changeant juste le nom, il n'est PAS personnalisé.

## 3. La règle du "so what"

Après chaque phrase de ton email, pose-toi la question : "et alors ?". Si tu ne peux pas répondre en <5 secondes, la phrase dégage.

Exemple :
- ❌ "Notre solution utilise l'IA pour optimiser vos processus" → so what ?
- ✅ "Vos SDR passent ~4h/semaine à chercher des emails LinkedIn — notre outil le fait en 20 min" → so what ? gain de temps mesurable.

## 4. Un CTA, un seul

Le CTA est la seule chose que ton prospect doit décider. Un email avec 3 CTA = 0 CTA.

Options par ordre de conversion :
1. "15 min mardi 14h ou mercredi 10h ?" (calendrier fermé, 4-8%)
2. "Ça vous parle ?" (yes/no, 6-12%)
3. "Un lien vers un vidéo de 90 sec, ça vous va ?" (2-4%)
4. "Envie de tester gratuitement ?" (0.5-2%)

## 5. La séquence à 3 pas (pas 7, pas 12)

Après 3 emails sans réponse, tu passes en spam définitif de son cerveau. La séquence qui marche :

- **Jour 0** : email principal (personnalisé, 60-80 mots)
- **Jour 3** : bump court, "revenu vers cet email, une pensée ?"
- **Jour 7** : rupture définitive, "je ferme la discussion, dis-moi si autre timing"

**Ne fais PAS** : email 4, 5, 6, 7, 8. Tu deviens du spam.

## 6. Le "break-up email" marche à 2-3%

Le dernier email de rupture — "je clôture la conversation, je respecte ton silence" — génère souvent le meilleur taux de réponse. Pourquoi ? Loss aversion : ton prospect a horreur de fermer une porte.

## 7. Longueur idéale : 60-90 mots

Mesures sur >1M emails cold analysés :

- 0-40 mots : 3.8% de réponse
- 40-80 mots : 8.5% (pic)
- 80-120 mots : 6.2%
- >120 mots : 2.1% (chute)

**Tu peux dire ce que tu veux en 80 mots.** Si tu ne peux pas, ton positionnement n'est pas clair.

## 8. Aucun jargon marketing

Liste noire absolue : "solution", "innovant", "révolutionnaire", "leader", "expertise", "accompagner", "j'espère que ce message vous trouve bien".

Chacun de ces mots divise ton taux de réponse par 2.

## 9. La signature ultra-minimaliste

- Prénom seul
- Pas de logo image (spam-flag)
- Pas de citation "success is a journey"
- Pas de disclaimer légal 15 lignes
- Optionnel : lien vers 1 ressource utile (post LinkedIn, article)

## 10. Envoie de ton domaine principal, pas d'un domaine "shadow"

Beaucoup d'agences envoient depuis \`get-boxeb2b.com\` au lieu de \`boxeb2b.com\` pour "protéger le domaine principal". Mauvaise idée en 2026 : les filtres anti-spam pénalisent les domaines de moins d'1 an.

**Utilise ton vrai domaine + un warm-up de 4 semaines minimum** avant tout envoi massif.

## Le workflow BOXEB2B pour ces 10 principes

Dans Engage, l'IA GPT-5.2 applique par défaut les règles 1-4-7-8-9 quand elle génère tes emails. Tu peux régénérer autant que tu veux jusqu'à obtenir un texte que tu enverrais toi-même à un ami.
        `,
    },
    {
        slug: "ai-prospecting-gpt-lead-gen-b2b",
        title: "AI prospecting : comment GPT change la génération de leads B2B en 2026",
        description:
            "GPT-5.2 dans la prospection B2B — au-delà du hype : ce que l'IA fait vraiment bien, ce qu'elle fait mal, et comment l'utiliser sans se ruiner ni s'endormir.",
        keywords: "AI prospecting, GPT-5.2, IA prospection B2B, ChatGPT sales, automatisation lead gen, IA email cold",
        published_at: "2026-06-28",
        author: "Équipe BOXEB2B",
        cover_gradient: "from-brand-violet to-brand-cyan",
        emoji: "🤖",
        read_min: 6,
        body: `
Depuis GPT-4 fin 2023, chaque startup lead gen se prétend "AI-powered". En 2026 avec GPT-5.2, le tri s'est fait. Voici ce qui marche vraiment, ce qui est du bullshit marketing, et comment BOXEB2B se positionne honnêtement.

## Ce que GPT-5.2 fait très bien en prospection B2B

### 1. Transformer une intention en filtre
"Trouve-moi des CTO de scale-ups SaaS berlinoises fondées après 2021" → filtre structuré \`{industries:["SaaS"], sizes:["51-200"], countries:["Germany"], title_keywords:["CTO"], founded_year_min:2021}\`.

Gain : ce que faisait un ops SDR en 5 min, GPT le fait en 2 secondes avec 95% de précision.

### 2. Génération d'emails personnalisés en masse
Un email + les données du lead (industrie, taille, poste) = un email adapté qui **respecte ton ton** si tu lui donnes 2-3 exemples de tes anciens emails.

Gain mesuré : de 60 emails/jour écrits à la main → 300 emails/jour avec relecture rapide.

### 3. Scoring de leads sur multiples critères pondérés
GPT peut lire une fiche lead complète et te dire : "priorité 87/100, raison : ils viennent d'embaucher un VP Sales + leur stack tech matche ta cible + article de presse sur leur difficulté à recruter des SDR".

Gain : tri qualitatif sur 500 leads en 10 min au lieu de 3 jours.

### 4. Résumés + suggestions d'actions
"Voilà ce que je sais de ce prospect, quelle serait la meilleure approche ?" → réponse structurée + template à envoyer.

## Ce que GPT-5.2 fait mal (ne te fais pas avoir)

### 1. Trouver des emails
GPT peut **deviner** un email pattern (\`prenom.nom@societe.fr\`) mais ne peut pas **vérifier** qu'il existe. Pour de vrai, il faut Hunter.io, Dropcontact, ou équivalent.

Trap : les outils qui prétendent "AI email finder à 5€/mois" — c'est du guessing pur, taux de bounce 40-60%.

### 2. Prédire un cycle de vente
GPT peut te dire "prospect intéressant" mais ne peut PAS te dire "ils achèteront dans 3 semaines". Aucune IA ne peut lire le budget interne d'une boîte que tu ne connais pas.

### 3. Remplacer un vrai discovery call
Les "meetings booked by AI" existent en démo, pas en prod. Un discovery de 30 min avec un humain reste 10x plus utile qu'un questionnaire IA.

### 4. Comprendre le contexte culturel local
GPT-5.2 écrit un excellent email en anglais américain. En français français, correct. En français guyanais avec les codes locaux, moyen. Toujours relire pour le contexte local.

## Le vrai calcul économique de l'IA en prospection

### Sans IA (SDR seul, workflow manuel)
- 60 emails écrits/jour × 20 jours = 1200 emails/mois
- Coût SDR : 3500 €/mois brut
- Coût / email : **~3 €**
- Taux de réponse moyen : 1-3%
- Coût / réponse : 100-300 €

### Avec IA + outils SaaS
- 300 emails générés/jour × 20 jours = 6000 emails/mois
- Coût SDR (relecture) : 2000 €/mois (0.5 ETP)
- Coût outils (IA + enrichissement) : 200 €/mois
- Coût / email : **~0.37 €**
- Taux de réponse moyen : 3-6% (mieux car mieux personnalisé)
- Coût / réponse : 6-12 €

**Économie : 15-25x sur le coût par réponse.**

## Comment BOXEB2B utilise GPT-5.2 (honnêtement)

- **Prospecting** : conversion NL → filtre (0 crédit user, on absorbe le coût GPT)
- **Email generation** : 1 crédit par séquence complète (3 emails)
- **Copilot** : chat contextuel qui a accès à tes leads/campagnes réels, 1 crédit par question
- **Scoring** : gratuit à l'import (déjà calculé)
- **Email guessing** (fallback IA) : 1 crédit par email deviné

Ce qu'on **ne fait pas** avec l'IA (encore) :
- Voice AI cold calling (arrivée en 2027 chez nous)
- Video prospecting IA
- Autonomous agents qui prospectent sans supervision

## Le futur : l'IA agent

D'ici 2027, l'IA autonome pourra probablement :
- Faire du research pré-call (5 min de préparation qui prenait 30 min)
- Répondre à 40% des objections communes par SMS/WhatsApp
- Proposer des créneaux, reprogrammer, envoyer les rappels

Mais elle ne remplacera **jamais** le moment humain où quelqu'un dit "je vous fais confiance, on y va". C'est là qu'un SDR gagne son salaire.
        `,
    },
];

export function findArticle(slug) {
    return ARTICLES.find((a) => a.slug === slug) || null;
}
